import apiClient from './apiClient';

export interface HostBankAccount {
  accountName: string;
  accountNumber: string;
  bankCode: string;
  bankName: string;
}

export interface Host {
  id: string;
  code?: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  email: string;
  phone: string | null;
}

export interface HostPayout {
  id: string;
  host: Host;
  amount: number;
  currency: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
  bankAccount: HostBankAccount;
  requestedAt: string;
  processedAt?: string;
  completedAt?: string;
  failedAt?: string;
  failedReason?: string;
  paystackTransferCode?: string;
  adminNotes?: string;
  retryCount?: number;
}

export interface HostPayoutsResponse {
  success: boolean;
  payouts: HostPayout[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

export interface HostPayoutActionResponse {
  success: boolean;
  message: string;
  payout: {
    id: string;
    status: string;
    transferCode?: string;
    completedAt?: string;
    refundedAmount?: number;
    reason?: string;
  };
}

export const hostPayoutsService = {
  /**
   * Get list of host payouts with optional filtering
   */
  getPayouts: async (params?: {
    status?: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled' | 'all';
    limit?: number;
    offset?: number;
  }): Promise<HostPayoutsResponse> => {
    // Convert offset to page number for backend API
    const page = params?.offset && params?.limit ? Math.floor(params.offset / params.limit) + 1 : 1;

    const queryParams: any = {
      page,
      limit: params?.limit || 50,
    };

    // Only add status if it's not 'all'
    if (params?.status && params.status !== 'all') {
      queryParams.status = params.status;
    }

    const response = await apiClient.get('/api/v1/admin/host-payouts', { params: queryParams });

    // Backend returns { success: true, data: { payouts, pagination } }
    // We need to extract the data and return it in the expected format
    if (response.data.success && response.data.data) {
      return {
        success: true,
        payouts: response.data.data.payouts || [],
        pagination: response.data.data.pagination || { total: 0, limit: 50, offset: 0, hasMore: false }
      };
    }

    return response.data;
  },

  /**
   * Approve a host payout and initiate Paystack transfer
   */
  approvePayout: async (payoutId: string): Promise<HostPayoutActionResponse> => {
    const response = await apiClient.post(`/api/v1/admin/host-payouts/${payoutId}/approve`);
    return response.data;
  },

  /**
   * Reject a host payout with reason
   */
  rejectPayout: async (payoutId: string, reason: string): Promise<HostPayoutActionResponse> => {
    const response = await apiClient.post(`/api/v1/admin/host-payouts/${payoutId}/reject`, {
      reason,
    });
    return response.data;
  },

  /**
   * Retry a failed host payout
   */
  retryPayout: async (payoutId: string, adminNotes?: string): Promise<HostPayoutActionResponse> => {
    const response = await apiClient.post(`/api/v1/admin/host-payouts/${payoutId}/retry`, {
      adminNotes,
    });
    return response.data;
  },
};
